import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../../App";
import { isUsRegion } from "../../config/regions";
import { useLanguage } from "../../i18n/LanguageContext";
import { useNavigate } from "react-router-dom";
import { useSession } from "../../SessionContext";
import { useSubjects } from "../../SubjectContext";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import { ScrollArea } from "../../components/ui/scroll-area";
import { trialQuotaSentence } from "../../i18n/quotaCopy";
import { freeCaseBannerCounts } from "../../lib/freeCaseBannerCounts";
import { notifyCasesChanged } from "../../lib/caseListRefresh";
import { track } from "../../lib/analytics";
import { caseScenarioById, scenarioFromSubmitArg } from "../../lib/caseScenarios";
import CaseScenarioChips from "../../components/CaseScenarioChips";
import CaseScenarioHint from "../../components/CaseScenarioHint";
import {
  Send, Shield, Loader2, ClipboardList, ImagePlus, X, Plus,
} from "lucide-react";
import Sidebar from "./Sidebar";
import CouncilSelector from "./CouncilSelector";
import ProcessingProgress from "./ProcessingProgress";
import ResultsDisplay from "./ResultsDisplay";
import SubmittedCaseSummary from "./SubmittedCaseSummary";
import SectionActionBar from "./SectionActionBar";
import FollowupChat from "./FollowupChat";
import { useFileUpload } from "./hooks/useFileUpload";
import { useQueryPolling } from "./hooks/useQueryPolling";
import { useProseScale } from "./hooks/useProseScale";
import { useCouncilPersonas } from "../../hooks/useCouncilPersonas";
import { useEntitlements } from "../../hooks/useEntitlements";
import PaywallModal from "../../components/PaywallModal";
import { MODEL_COLORS, MODEL_NAMES, BACKING_MODELS, BACKING_MODEL_NAMES } from "./constants";
import DisclaimerNotice from "../../components/DisclaimerNotice";
import { fetchDisclaimer, postDisclaimerConsent } from "../../api/disclaimer";
import {
  disclaimerConsentIsCurrent,
  isDisclaimerRequired,
  showDisclaimerCheckbox,
  storedDisclaimerVersion,
} from "../../lib/disclaimerConsent";
import CaseForChip from "../../components/CaseForChip";
import HealthContextRow from "../../components/HealthContextRow";
import { HistoryConsentDialog } from "../../components/history/AddRecordModal";
import CaseFilesNotSavedDialog from "../../components/history/CaseFilesNotSavedDialog";
import {
  consumeHistoryFilesSkipped,
  isActiveFinishedQuery,
  rememberHistoryFilesSkipped,
} from "./historyFilesNotice";
const QueryPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const session = useSession();
  const {
    activeSubject,
    activeSubjectId,
    caseOneOff,
    refreshSubjects,
  } = useSubjects();
  const [saveFilesToHistory, setSaveFilesToHistory] = useState(true);
  const [historyConsentOpen, setHistoryConsentOpen] = useState(false);
  const [historyFilesNoticeOpen, setHistoryFilesNoticeOpen] = useState(false);
  const historyFilesSkippedByQuery = useRef(new Map());
  const activeQueryIdRef = useRef(null);
  const followupEndRef = useRef(null);

  const {
    queryText, setQueryText,
    queryId, setQueryId,
    loading, setLoading,
    result, setResult,
    followups, setFollowups,
    followupInput, setFollowupInput,
    followupLoading, setFollowupLoading,
    downloadingPdf, setDownloadingPdf,
    copiedId, setCopiedId,
    partialSynthesis,
    hasActiveSession,
    resetSession,
    interrupted, setInterrupted,
    selectedCouncil, setSelectedCouncil,
  } = session;

  // Council personas drive the per-query selector. An empty selection is
  // automatic: the server picks specialists after the case is submitted.
  const {
    personas: councilPersonas,
    loading: councilLoading,
  } = useCouncilPersonas();

  // Trial / free entitlement summary drives the banner + keeps counters
  // fresh after each submission (quota is consumed when the query is created).
  const { entitlements, refresh: refreshEntitlements } = useEntitlements();
  const { fetchBackendUser, user } = useAuth();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [followupsLimit, setFollowupsLimit] = useState(3);
  const [followupsRemaining, setFollowupsRemaining] = useState(null);
  const [followupFilesRemaining, setFollowupFilesRemaining] = useState(null);
  const [includeHealthProfile, setIncludeHealthProfile] = useState(true);
  const [extendedInitial, setExtendedInitial] = useState({});
  const extendedLoadSeq = useRef(0);
  const [disclaimerText, setDisclaimerText] = useState("");
  const [disclaimerVersion, setDisclaimerVersion] = useState("");
  const [disclaimerLoaded, setDisclaimerLoaded] = useState(false);
  const [ackedVersion, setAckedVersion] = useState("");
  const [disclaimerGateForced, setDisclaimerGateForced] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [submitLocked, setSubmitLocked] = useState(false);
  const [submittedScenario, setSubmittedScenario] = useState(null);
  const submitLockRef = useRef(false);
  const submitScenarioRef = useRef(null);
  const profileFlags = {
    tier_a_complete: Boolean(
      caseOneOff ? false : activeSubject?.tier_a_complete
        ?? user?.health_profile_summary?.tier_a_complete,
    ),
    tier_extended_complete: Boolean(
      caseOneOff ? false : activeSubject?.tier_extended_complete
        ?? user?.health_profile_summary?.tier_extended_complete,
    ),
  };

  const reloadExtended = useCallback(async () => {
    const seq = ++extendedLoadSeq.current;
    if (caseOneOff) {
      setExtendedInitial({});
      return;
    }
    try {
      let data;
      if (activeSubjectId && activeSubject?.kind !== "self") {
        const res = await axios.get(`${API}/subjects/${activeSubjectId}`);
        const detail = res.data?.data;
        data = {
          tier_a_complete: detail?.tier_a_complete,
          health_profile: detail?.health_profile,
        };
      } else {
        const res = await axios.get(`${API}/profile/health`);
        data = res.data?.data;
      }
      if (seq !== extendedLoadSeq.current) return;
      setExtendedInitial(data?.health_profile?.tier_extended || {});
      if (data?.tier_a_complete === true) {
        setIncludeHealthProfile(true);
      }
    } catch {
      /* non-blocking; do not keep another person's answers on screen */
    }
  }, [activeSubjectId, activeSubject?.kind, caseOneOff]);

  useEffect(() => {
    setExtendedInitial({});
    reloadExtended();
  }, [reloadExtended]);

  const handleSectionSaved = async () => {
    await reloadExtended();
    await refreshSubjects?.();
    await fetchBackendUser?.();
    refreshEntitlements();
  };

  const storedVersion = storedDisclaimerVersion(user);
  useEffect(() => {
    if (disclaimerGateForced) return;
    setAckedVersion((current) => {
      if (disclaimerVersion && disclaimerConsentIsCurrent(current, disclaimerVersion)) {
        return current;
      }
      return storedVersion;
    });
  }, [storedVersion, disclaimerGateForced, disclaimerVersion]);

  useEffect(() => {
    let cancelled = false;
    setDisclaimerLoaded(false);
    fetchDisclaimer(language)
      .then((payload) => {
        if (cancelled) return;
        setDisclaimerText(payload.text.trim());
        setDisclaimerVersion(payload.text_version);
      })
      .catch(() => {
        if (cancelled) return;
        setDisclaimerText("");
        setDisclaimerVersion("");
      })
      .finally(() => {
        if (!cancelled) setDisclaimerLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [language]);

  const needsDisclaimerCheckbox = showDisclaimerCheckbox({
    storedVersion: ackedVersion,
    serverVersion: disclaimerVersion,
    serverLoaded: disclaimerLoaded,
    forced: disclaimerGateForced,
  });

  const acceptDisclaimer = async () => {
    if (consentSaving || !disclaimerVersion) return;
    setConsentSaving(true);
    try {
      await postDisclaimerConsent({
        text_version: disclaimerVersion,
        platform: "web",
        locale: language,
      });
      setAckedVersion(disclaimerVersion);
      setDisclaimerGateForced(false);
      await fetchBackendUser?.();
    } catch (err) {
      toast.error(
        isDisclaimerRequired(err)
          ? t("query.disclaimerRequired")
          : t("healthProfile.disclaimerRequired"),
      );
    } finally {
      setConsentSaving(false);
    }
  };

  const trialActive = Boolean(entitlements?.trial_active);
  const trialDaysRemaining = (() => {
    if (!trialActive || !entitlements?.trial_ends_at) return 0;
    const ends = new Date(entitlements.trial_ends_at).getTime();
    if (Number.isNaN(ends)) return 0;
    return Math.max(0, Math.ceil((ends - Date.now()) / (24 * 60 * 60 * 1000)));
  })();

  const showTrialBanner = trialActive;
  const trialRemaining = entitlements?.trial_daily_cases_remaining ?? 0;
  const trialDailyExhausted = trialActive && trialRemaining <= 0;
  const submitBlocked = entitlements?.can_submit === false;
  const trialBannerText = showTrialBanner
    ? trialQuotaSentence(t, language, {
        days: trialDaysRemaining,
        remaining: trialRemaining,
        blocked: submitBlocked,
      })
    : "";

  const showFreeBanner =
    entitlements &&
    !trialActive &&
    (entitlements.free_cases_remaining > 0 ||
      (entitlements.onboarding_bonus_cases_remaining ?? 0) > 0 ||
      entitlements.plan === "free");
  const freeCounts = freeCaseBannerCounts(entitlements);
  const freeBannerText = showFreeBanner
    ? t("query.freeCasesBanner")
        .replace("{remaining}", String(freeCounts.remaining))
        .replace("{limit}", String(freeCounts.limit))
    : "";

  const showBonusCta =
    entitlements &&
    !entitlements.onboarding_bonus_granted &&
    (entitlements.onboarding_bonus_cases_remaining ?? 0) === 0 &&
    (trialActive || entitlements.plan === "free");
  // HealthContextRow shows the same CTA for self while the Health Profile is incomplete.
  const chipShowsBonusCta =
    Boolean(showBonusCta) &&
    !result &&
    !caseOneOff &&
    activeSubject?.kind === "self" &&
    Boolean(activeSubject?.tier_a_complete) &&
    activeSubject?.tier_extended_complete === false;
  const showPostTrialPaywallHint =
    entitlements && !trialActive && entitlements.can_submit === false;

  // Drop persona ids the org no longer offers. Empty stays empty (automatic).
  useEffect(() => {
    if (councilLoading) return;
    if (councilPersonas.length === 0) return;

    const validIds = new Set(councilPersonas.map((p) => p.id));

    if (selectedCouncil === null || !Array.isArray(selectedCouncil)) {
      setSelectedCouncil([]);
      return;
    }

    const sanitized = selectedCouncil.filter((id) => validIds.has(id));
    if (sanitized.length === selectedCouncil.length) return;

    setSelectedCouncil(sanitized);
    if (selectedCouncil.length > 0) {
      toast.info(t("query.councilSelectionUpdated"));
    }
  }, [councilLoading, councilPersonas, selectedCouncil, setSelectedCouncil, t]);

  const [processingHasAttachments, setProcessingHasAttachments] = useState(false);

  const proseScale = useProseScale();

  const maxFilesPerCase =
    typeof entitlements?.max_files_per_case === "number"
      ? entitlements.max_files_per_case
      : 8;
  const maxFollowupFilesTotal =
    typeof entitlements?.max_followup_files_total === "number"
      ? entitlements.max_followup_files_total
      : 8;
  const followupFilesUsed = followups.reduce(
    (sum, fu) => sum + (Array.isArray(fu?.files) ? fu.files.length : 0),
    0,
  );
  const effectiveFollowupFilesRemaining =
    typeof followupFilesRemaining === "number"
      ? followupFilesRemaining
      : Math.max(0, maxFollowupFilesTotal - followupFilesUsed);

  const {
    attachments, handleFileUpload, removeAttachment, formatFileSize,
    clearAttachments, fileInputRef, ACCEPT_STRING, FILE_ICONS,
  } = useFileUpload({ maxFiles: maxFilesPerCase });

  const {
    attachments: followupAttachments,
    handleFileUpload: handleFollowupFileUpload,
    removeAttachment: removeFollowupAttachment,
    clearAttachments: clearFollowupAttachments,
    fileInputRef: followupFileInputRef,
    ACCEPT_STRING: followupAcceptString,
    FILE_ICONS: followupFileIcons,
  } = useFileUpload({ maxFiles: effectiveFollowupFilesRemaining });

  const {
    estimatedTime, elapsedTime,
    stopTimer, formatTime,
    startPolling, recoverPolling, resumeInterruptedPolling,
    getCurrentStage, getProgressPercent, PIPELINE_STAGES,
    setPartialSynthesis: setPollingPartialSynthesis,
  } = useQueryPolling();

  // Sync partial synthesis from polling hook to session context
  const { setPartialSynthesis: setSessionPartialSynthesis } = session;

  // Recover polling on mount — handles both F5 refresh and tab-switch
  useEffect(() => {
    if (result) return; // already have result, nothing to recover

    const pollCallbacks = {
      onCompleted: (completedData, finishedQueryId) => {
        const finishedId = finishedQueryId || completedData?.query_id;
        if (consumeHistoryFilesSkipped(historyFilesSkippedByQuery.current, finishedId)) {
          setHistoryFilesNoticeOpen(true);
        }
        if (!isActiveFinishedQuery(activeQueryIdRef.current, finishedId)) return;
        setResult(completedData);
        setQueryId(finishedId);
        setFollowups(completedData.followups || []);
        if (typeof completedData.followups_limit === "number") {
          setFollowupsLimit(completedData.followups_limit);
        }
        if (typeof completedData.followups_remaining === "number") {
          setFollowupsRemaining(completedData.followups_remaining);
        }
        if (typeof completedData.followup_files_remaining === "number") {
          setFollowupFilesRemaining(completedData.followup_files_remaining);
        }
        setProcessingHasAttachments(false);
        setLoading(false);
        toast.success(t('query.success'));
      },
      onError: (errMsg, finishedQueryId) => {
        if (consumeHistoryFilesSkipped(historyFilesSkippedByQuery.current, finishedQueryId)) {
          setHistoryFilesNoticeOpen(true);
        }
        if (!isActiveFinishedQuery(activeQueryIdRef.current, finishedQueryId)) return;
        setProcessingHasAttachments(false);
        setLoading(false);
        if (errMsg !== "auth" && errMsg !== "timeout") {
          toast.error(errMsg || t('common.error'));
        }
      },
    };

    // Case 1: F5 refresh — SessionContext marked as interrupted
    if (interrupted && queryId) {
      setInterrupted(false);
      setLoading(true);
      const recovery = resumeInterruptedPolling(queryId, pollCallbacks);
      setProcessingHasAttachments(recovery.hasAttachments);
      activeQueryIdRef.current = recovery.queryId;
      rememberHistoryFilesSkipped(
        historyFilesSkippedByQuery.current,
        recovery.queryId,
        recovery.historyFilesSkipped,
      );
      return;
    }

    // Case 2: tab-switch — polling state in sessionStorage
    {
      const recovered = recoverPolling(pollCallbacks);
      if (recovered) {
        setQueryId(recovered.queryId);
        setProcessingHasAttachments(recovered.hasAttachments);
        activeQueryIdRef.current = recovered.queryId;
        rememberHistoryFilesSkipped(
          historyFilesSkippedByQuery.current,
          recovered.queryId,
          recovered.historyFilesSkipped,
        );
        setLoading(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const releaseSubmitLock = () => {
    submitLockRef.current = false;
    setSubmitLocked(false);
    setLoading(false);
  };

  const abortSubmittedScenario = () => {
    releaseSubmitLock();
    setSubmittedScenario(null);
    submitScenarioRef.current = null;
  };

  const handleSubmit = async (options) => {
    if (submitLockRef.current) return;
    const { scenario, alreadyTracked } = scenarioFromSubmitArg(options);
    if (scenario && attachments.length === 0) return;
    if (!queryText.trim() && attachments.length === 0) {
      toast.error(t('common.error'));
      return;
    }
    if (needsDisclaimerCheckbox || consentSaving) {
      toast.error(t("query.disclaimerRequired"));
      return;
    }
    if (submitBlocked || councilLoading) return;

    submitLockRef.current = true;
    submitScenarioRef.current = scenario;
    setSubmittedScenario(scenario);
    setSubmitLocked(true);
    if (scenario && !alreadyTracked) {
      track("case_scenario_run", { scenario });
    }

    setLoading(true);
    setResult(null);
    const hadAttachments = attachments.length > 0;
    setProcessingHasAttachments(hadAttachments);

    try {
      let response;
      
      // Empty selection omits council_override so the server selects specialists.
      const councilOverride =
        Array.isArray(selectedCouncil) && selectedCouncil.length > 0
          ? selectedCouncil
          : null;

      const usRegion = isUsRegion();
      const filesOnlyText = usRegion ? '' : t('query.filesOnly');

      if (attachments.length > 0) {
        const formData = new FormData();
        formData.append('query_text', queryText || filesOnlyText);
        if (scenario) formData.append('scenario', scenario);
        if (!usRegion) formData.append('language', language);
        attachments.forEach(att => {
          formData.append('files', att.file);
        });
        if (councilOverride) {
          councilOverride.forEach((id) => formData.append('council_override', id));
        }
        formData.append(
          'include_health_profile',
          includeHealthProfile && profileFlags.tier_a_complete ? 'true' : 'false',
        );
        if (caseOneOff) {
          formData.append('subject_mode', 'one_off');
        } else if (activeSubjectId) {
          formData.append('subject_id', activeSubjectId);
          if (saveFilesToHistory) formData.append('save_files_to_history', 'true');
        }
        response = await axios.post(`${API}/council/query-with-files`, formData);
      } else {
        const payload = { query_text: queryText };
        if (!usRegion) payload.language = language;
        if (councilOverride) payload.council_override = councilOverride;
        payload.include_health_profile =
          includeHealthProfile && profileFlags.tier_a_complete;
        if (caseOneOff) {
          payload.subject_mode = 'one_off';
        } else if (activeSubjectId) {
          payload.subject_id = activeSubjectId;
        }
        response = await axios.post(`${API}/council/query`, payload);
      }

      const data = response.data?.data;
      if (data?.query_id && data?.status === "processing") {
        rememberHistoryFilesSkipped(
          historyFilesSkippedByQuery.current,
          data.query_id,
          data.history_files_skipped,
        );
        activeQueryIdRef.current = data.query_id;
        setQueryId(data.query_id);
        notifyCasesChanged();
        refreshEntitlements();
        startPolling(data.query_id, data.estimated_seconds, {
          onCompleted: (completedData, finishedQueryId) => {
            const finishedId = finishedQueryId || completedData?.query_id;
            if (consumeHistoryFilesSkipped(historyFilesSkippedByQuery.current, finishedId)) {
              setHistoryFilesNoticeOpen(true);
            }
            if (!isActiveFinishedQuery(activeQueryIdRef.current, finishedId)) return;
            setResult(completedData);
            setQueryId(finishedId);
            setFollowups(completedData.followups || []);
            if (typeof completedData.followups_limit === "number") {
              setFollowupsLimit(completedData.followups_limit);
            }
            if (typeof completedData.followups_remaining === "number") {
              setFollowupsRemaining(completedData.followups_remaining);
            }
            if (typeof completedData.followup_files_remaining === "number") {
              setFollowupFilesRemaining(completedData.followup_files_remaining);
            }
            setProcessingHasAttachments(false);
            clearAttachments();
            setSelectedScenario(null);
            releaseSubmitLock();
            toast.success(t('query.success'));
          },
          onError: (errMsg, finishedQueryId) => {
            if (consumeHistoryFilesSkipped(historyFilesSkippedByQuery.current, finishedQueryId)) {
              setHistoryFilesNoticeOpen(true);
            }
            if (!isActiveFinishedQuery(activeQueryIdRef.current, finishedQueryId)) return;
            setProcessingHasAttachments(false);
            abortSubmittedScenario();
            if (errMsg !== "auth" && errMsg !== "timeout") {
              toast.error(errMsg || t('common.error'));
            }
          },
        }, {
          hasAttachments: hadAttachments,
          historyFilesSkipped: Boolean(data.history_files_skipped),
        });
      } else if (data?.synthesis) {
        activeQueryIdRef.current = data.query_id;
        setResult(data);
        setQueryId(data.query_id);
        notifyCasesChanged();
        clearAttachments();
        setSelectedScenario(null);
        releaseSubmitLock();
        if (data.history_files_skipped) {
          setHistoryFilesNoticeOpen(true);
        }
        toast.success(t('query.success'));
      } else if (response.data?.error) {
        abortSubmittedScenario();
        toast.error(response.data.error.message || t('common.error'));
      } else {
        abortSubmittedScenario();
        toast.error(t('common.error') || 'Unexpected server response');
      }
    } catch (error) {
      setProcessingHasAttachments(false);
      stopTimer();
      const keepScenario = error.response?.data?.error?.code === "history_consent_required";
      if (keepScenario) releaseSubmitLock();
      else abortSubmittedScenario();
      if (isDisclaimerRequired(error)) {
        setDisclaimerGateForced(true);
        toast.error(t("query.disclaimerRequired"));
      } else if (error.response?.data?.error?.code === "history_consent_required") {
        setHistoryConsentOpen(true);
        toast.error(t("history.consentRequired"));
      } else if (error.response?.data?.error?.code === "history_storage_full") {
        toast.error(t("history.storageFull"));
      } else if (error.response?.status === 401) {
        toast.error(t('common.authError') || 'Session expired. Please log in again.');
      } else if (error.response?.status === 402) {
        const code = error.response.data?.error?.code;
        const msg =
          code === "trial_daily_limit"
            ? t("query.trialDailyLimit")
            : code === "trial_expired"
              ? t("query.trialExpired")
              : code === "free_quota_exhausted"
                ? t("billing.freeQuotaExhausted")
                : code === "subscription_exhausted"
                  ? t("billing.subscriptionExhausted")
                  : error.response.data?.error?.message || t("billing.paymentRequired");
        toast.error(msg);
        setPaywallOpen(true);
        refreshEntitlements();
      } else if (error.response?.status === 429) {
        const code = error.response.data?.error?.code;
        const msg =
          code === "trial_daily_limit"
            ? t("query.trialDailyLimit")
            : code === "trial_expired"
              ? t("query.trialExpired")
              : error.response.data?.error?.message || t("query.rateLimit");
        toast.error(msg);
        refreshEntitlements();
      } else {
        const detail = error.response?.data?.detail;
        const code =
          (detail && typeof detail === "object" && detail.code) ||
          error.response?.data?.error?.code;
        const maxFromDetail =
          detail && typeof detail === "object" && typeof detail.max_files === "number"
            ? detail.max_files
            : maxFilesPerCase;
        const msg =
          code === "too_many_files"
            ? t("query.tooManyFiles").replace("{max}", String(maxFromDetail))
            : code === "file_too_large"
              ? t("query.fileTooLarge")
              : code === "attachments_too_large"
                ? t("query.attachmentsTooLarge")
                : (typeof detail === "string" && detail) ||
                  error.response?.data?.error?.message ||
                  error.message ||
                  t("common.error");
        toast.error(msg);
      }
    }
  };

  const executeDownload = async (docType, format = "pdf") => {
    if (!queryId) {
      toast.error(t("query.pdfNoQuery") || "Нет данных для скачивания");
      return;
    }
    setDownloadingPdf(`${docType}-${format}`);
    try {
      const response = await axios.get(
        `${API}/council/query/${queryId}/export/${docType}?format=${format}`,
        { responseType: "blob" }
      );
      const mimeType =
        format === "docx"
          ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          : "application/pdf";
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `consilium_${docType}_${queryId}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(format === "docx" ? t("query.docxDownloaded") : t("query.pdfDownloaded"));
    } catch (error) {
      let msg = t("query.pdfError") || "Ошибка скачивания";
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch {}
      } else if (error.response?.data?.detail) {
        msg = error.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setDownloadingPdf(null);
    }
  };

  // B2C: exports are a consumer second-opinion report. Physician attestation is
  // no longer the export gate — download directly. The persistent disclaimer on
  // the results and in the exported document carries the compliance messaging.
  const handleDownload = async (docType, format = "pdf") => {
    if (!queryId) {
      toast.error(t("query.pdfNoQuery") || "Нет данных для скачивания");
      return;
    }

    await executeDownload(docType, format);
  };

  const handleFollowup = async () => {
    const hasText = Boolean(followupInput.trim());
    const hasFiles = followupAttachments.length > 0;
    if (!queryId || followupLoading || (!hasText && !hasFiles)) return;
    if (typeof followupsRemaining === "number" && followupsRemaining <= 0) return;
    if (needsDisclaimerCheckbox || consentSaving) {
      toast.error(t("query.disclaimerRequired"));
      return;
    }
    const question = followupInput.trim();
    setFollowupInput("");
    const attachmentsToSend = [...followupAttachments];
    clearFollowupAttachments();
    setFollowupLoading(true);
    try {
      let response;
      if (attachmentsToSend.length > 0) {
        const formData = new FormData();
        formData.append("question", question);
        if (!isUsRegion()) formData.append("language", language);
        attachmentsToSend.forEach((att) => {
          formData.append("files", att.file);
        });
        response = await axios.post(
          `${API}/council/query/${queryId}/followup-with-files`,
          formData,
        );
      } else {
        const followupPayload = { question };
        if (!isUsRegion()) followupPayload.language = language;
        response = await axios.post(`${API}/council/query/${queryId}/followup`, followupPayload);
      }
      const entry = response.data?.data;
      if (entry) {
        setFollowups(prev => [...prev, entry]);
        setFollowupsRemaining((prev) =>
          typeof prev === "number" ? Math.max(0, prev - 1) : prev,
        );
        const addedFiles = Array.isArray(entry.files) ? entry.files.length : 0;
        if (addedFiles > 0) {
          setFollowupFilesRemaining((prev) => {
            if (typeof prev === "number") return Math.max(0, prev - addedFiles);
            return Math.max(0, maxFollowupFilesTotal - followupFilesUsed - addedFiles);
          });
        }
        setTimeout(() => followupEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch (error) {
      if (isDisclaimerRequired(error)) {
        setDisclaimerGateForced(true);
        toast.error(t("query.disclaimerRequired"));
      } else {
        const detail = error.response?.data?.detail;
        const code = error.response?.data?.error?.code
          || (detail && typeof detail === "object" && detail.code)
          || error.response?.data?.detail?.code;
        if (error.response?.status === 402 && code === "followup_limit_exceeded") {
          setFollowupsRemaining(0);
          toast.error(t("query.followupLimitExceeded"));
        } else if (code === "followup_attachments_exhausted") {
          const remaining =
            detail && typeof detail === "object" && typeof detail.remaining === "number"
              ? detail.remaining
              : 0;
          setFollowupFilesRemaining(remaining);
          toast.error(t("query.followupAttachmentsExhausted"));
        } else if (code === "too_many_files") {
          const maxFromDetail =
            detail && typeof detail === "object" && typeof detail.max_files === "number"
              ? detail.max_files
              : effectiveFollowupFilesRemaining;
          toast.error(t("query.tooManyFiles").replace("{max}", String(maxFromDetail)));
        } else {
          toast.error(t('query.followupError') || 'Ошибка при отправке вопроса');
        }
      }
    } finally {
      setFollowupLoading(false);
    }
  };

  const handleCopy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success(t('query.copied') || 'Скопировано');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error(t('query.copyError'));
    }
  };

  const handleNewQuery = () => {
    if (loading) {
      const confirmed = window.confirm(t('query.confirmNewQueryForce'));
      if (!confirmed) return;
      stopTimer();
    }
    activeQueryIdRef.current = null;
    submitLockRef.current = false;
    submitScenarioRef.current = null;
    setSubmitLocked(false);
    setSelectedScenario(null);
    setSubmittedScenario(null);
    resetSession();
    clearAttachments();
  };

  const selectedMeta = caseScenarioById(selectedScenario);
  const submitLabel = selectedMeta ? t(selectedMeta.runKey) : t("query.submit");
  const scenarioNeedsFile = Boolean(selectedScenario) && attachments.length === 0;

  const handleScenarioToggle = (scenario) => {
    if (submitLockRef.current || loading) return;
    if (selectedScenario === scenario) {
      setSelectedScenario(null);
      return;
    }
    setSelectedScenario(scenario);
    track("case_scenario_selected", { scenario });
    if (attachments.length === 0) {
      track("case_scenario_picker_opened", { scenario });
      fileInputRef.current?.click();
    }
  };

  return (
    <div className="flex h-screen bg-background" data-testid="query-page">
      <Sidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-14 md:h-16 border-b border-border flex items-center justify-between px-4 md:px-6 pl-14 md:pl-6 bg-background">
          <div className="flex items-center gap-2 md:gap-3">
            <ClipboardList className="w-4 h-4 md:w-5 md:h-5 text-primary" />
            <h1 className="text-base md:text-lg font-semibold">{t('query.title')}</h1>
          </div>
          <div className="flex items-center gap-2">
          {hasActiveSession && (
            <Button
              data-testid="new-query-btn"
              size="sm"
              onClick={handleNewQuery}
              className="gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{t('nav.newQuery')}</span>
            </Button>
          )}
          </div>
        </header>

        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="max-w-6xl mx-auto p-4 md:p-5 space-y-4">
              {/* Trial / free entitlement banners */}
              {showTrialBanner && (
                <div
                  data-testid="trial-cases-banner"
                  className="flex items-center gap-2 text-xs md:text-sm rounded-md border border-primary/20 bg-primary/5 text-primary px-3 py-2"
                >
                  <Shield className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{trialBannerText}</span>
                  {(trialDailyExhausted || submitBlocked) && (
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs md:text-sm"
                      data-testid="increase-limit-btn"
                      onClick={() => setPaywallOpen(true)}
                    >
                      {t("query.increaseLimit")}
                    </Button>
                  )}
                </div>
              )}
              {showFreeBanner && (
                <div
                  data-testid="free-cases-banner"
                  className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs md:text-sm rounded-md border border-primary/20 bg-primary/5 text-primary px-3 py-2"
                >
                  <Shield className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{freeBannerText}</span>
                  {showPostTrialPaywallHint && (
                    <>
                      <span data-testid="post-trial-paywall-hint">
                        {t("query.postTrialPaywallHint")}
                      </span>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs md:text-sm"
                        data-testid="increase-limit-btn"
                        onClick={() => setPaywallOpen(true)}
                      >
                        {t("query.increaseLimit")}
                      </Button>
                    </>
                  )}
                </div>
              )}
              {showBonusCta && !chipShowsBonusCta && (
                <div
                  data-testid="onboarding-bonus-cta"
                  className="flex items-center gap-2 text-xs md:text-sm rounded-md border border-border bg-muted/40 text-foreground px-3 py-2"
                >
                  <span>{t("query.onboardingBonusCta")}</span>
                </div>
              )}
              {showPostTrialPaywallHint && !showFreeBanner && (
                <div
                  data-testid="post-trial-paywall-hint"
                  className="flex items-center gap-2 text-xs md:text-sm rounded-md border border-border bg-muted/40 text-foreground px-3 py-2"
                >
                  <span>{t("query.postTrialPaywallHint")}</span>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs md:text-sm"
                    data-testid="increase-limit-btn"
                    onClick={() => setPaywallOpen(true)}
                  >
                    {t("query.increaseLimit")}
                  </Button>
                </div>
              )}

              {/* Per-query council selection */}
              {!result && !loading && (
                <CouncilSelector
                  personas={councilPersonas}
                  loading={councilLoading}
                  disabled={loading}
                />
              )}

              {(loading || result) ? (
                <SubmittedCaseSummary
                  result={result}
                  queryText={queryText}
                  scenario={submittedScenario}
                />
              ) : (
              /* Query Input */
              <Card className="border border-border">
                <CardContent className="pt-6">
                  {!result && (
                    <div className="mb-4 pb-4 border-b space-y-3">
                      <HealthContextRow
                        bonusAvailable={Boolean(showBonusCta)}
                        initialExtended={extendedInitial}
                        onSaved={handleSectionSaved}
                      >
                        {({ links, bonus, dialog, openHealthSection }) => (
                          <div className="space-y-3">
                            <CaseForChip
                              includeProfile={includeHealthProfile}
                              onIncludeChange={setIncludeHealthProfile}
                              headerExtra={links}
                              onOpenSafety={() => openHealthSection("safety")}
                              onReview={(subject) => {
                                if (subject?.subject_id) {
                                  navigate(`/people/${subject.subject_id}`);
                                }
                              }}
                            />
                            {bonus}
                            {dialog}
                          </div>
                        )}
                      </HealthContextRow>
                    </div>
                  )}

                  <CaseScenarioChips
                    selectedId={selectedScenario}
                    disabled={submitLocked}
                    onToggle={handleScenarioToggle}
                  />

                  <Textarea
                    data-testid="query-input"
                    placeholder={
                      !caseOneOff && activeSubject?.kind === "dependent"
                        ? t("subjects.placeholderFor").replace(
                            "{name}",
                            activeSubject.display_name,
                          )
                        : t("query.placeholder")
                    }
                    value={queryText}
                    onChange={(e) => setQueryText(e.target.value)}
                    className="min-h-[80px] md:min-h-[120px] text-sm md:text-base resize-none border-0 focus-visible:ring-0 p-0 bg-transparent"
                    disabled={loading}
                  />
                  
                  {/* Attachments Preview */}
                  {attachments.length > 0 && !caseOneOff && activeSubject && (
                    <label className="mt-4 flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={saveFilesToHistory}
                        onChange={(e) => setSaveFilesToHistory(e.target.checked)}
                        data-testid="save-files-to-history"
                      />
                      <span>
                        {t("history.saveFilesToHistory").replace(
                          "{name}",
                          activeSubject.display_name || "",
                        )}
                      </span>
                    </label>
                  )}
                  {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                      {attachments.map((att) => (
                        <div key={att.id} className="relative group flex items-center gap-2 bg-muted/50 rounded-md px-3 py-2 pr-8 border">
                          {att.type === 'image' && att.preview ? (
                            <img src={att.preview} alt={att.name} className="w-10 h-10 object-cover rounded-sm" />
                          ) : (
                            <span className="text-xl">{FILE_ICONS[att.type] || '\uD83D\uDCCE'}</span>
                          )}
                          <div className="min-w-0">
                            <p className="text-xs font-medium truncate max-w-[120px]">{att.name}</p>
                            <p className="text-[10px] text-muted-foreground">{formatFileSize(att.size)}</p>
                          </div>
                          <button
                            data-testid={`remove-attachment-${att.id}`}
                            onClick={() => removeAttachment(att.id)}
                            className="absolute top-1 right-1 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <CaseScenarioHint
                    scenarioId={selectedScenario}
                    hasAttachments={attachments.length > 0}
                    onAttach={() => fileInputRef.current?.click()}
                    onClear={() => setSelectedScenario(null)}
                  />

                  {!result && needsDisclaimerCheckbox && (
                    <DisclaimerNotice
                      needsCheckbox
                      text={disclaimerText}
                      saving={consentSaving}
                      onAccept={acceptDisclaimer}
                    />
                  )}
                  
                  <div
                    className="mt-4 flex items-center justify-between gap-2 border-t pt-4"
                    data-testid="query-action-row"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept={ACCEPT_STRING}
                        multiple
                        className="hidden"
                        data-testid="file-input"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={loading || submitLocked}
                        className="gap-1.5 md:gap-2 flex-shrink-0 px-2 md:px-3"
                        data-testid="attach-file-btn"
                      >
                        <ImagePlus className="w-4 h-4" />
                        <span className="hidden sm:inline text-xs md:text-sm">{t('query.addFile')}</span>
                      </Button>
                      <p
                        className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground"
                        data-testid="query-pii-notice"
                      >
                        <Shield className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>{t('query.piiNotice')}</span>
                      </p>
                    </div>
                    <Button
                      data-testid="submit-query-btn"
                      onClick={() => {
                        if (selectedScenario) handleSubmit({ scenario: selectedScenario });
                        else handleSubmit();
                      }}
                      disabled={
                        loading ||
                        submitLocked ||
                        submitBlocked ||
                        consentSaving ||
                        needsDisclaimerCheckbox ||
                        councilLoading ||
                        (scenarioNeedsFile
                          || (!selectedScenario && !queryText.trim() && attachments.length === 0))
                      }
                      className="gap-1.5 md:gap-2 flex-shrink-0"
                      size="sm"
                    >
                      {loading || submitLocked ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="hidden sm:inline">{t('query.loading')}</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          <span>{submitLabel}</span>
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Processing Progress */}
              {loading && estimatedTime && (
                <ProcessingProgress
                  elapsedTime={elapsedTime}
                  estimatedTime={estimatedTime}
                  formatTime={formatTime}
                  getCurrentStage={getCurrentStage}
                  getProgressPercent={getProgressPercent}
                  PIPELINE_STAGES={PIPELINE_STAGES}
                  hasAttachments={processingHasAttachments}
                />
              )}

              {/* Results + Streaming Preview */}
              <ResultsDisplay
                result={result}
                partialSynthesis={partialSynthesis}
                loading={loading}
                queryId={queryId}
                proseClassName={proseScale.proseClassName}
                onCopy={handleCopy}
                onDownload={handleDownload}
                onNewQuery={handleNewQuery}
                downloadingPdf={downloadingPdf}
                copiedId={copiedId}
                proseScale={proseScale}
                disclaimerSentence={disclaimerText}
                personas={councilPersonas}
              />

              {result && needsDisclaimerCheckbox && (
                <DisclaimerNotice
                  needsCheckbox
                  text={disclaimerText}
                  saving={consentSaving}
                  onAccept={acceptDisclaimer}
                  className="mb-2"
                />
              )}

              {/* Follow-up Chat */}
              {result && (
                <FollowupChat
                  followups={followups}
                  followupInput={followupInput}
                  setFollowupInput={setFollowupInput}
                  followupLoading={followupLoading}
                  onSubmit={handleFollowup}
                  followupEndRef={followupEndRef}
                  followupsLimit={followupsLimit}
                  followupsRemaining={followupsRemaining}
                  followupAttachments={followupAttachments}
                  onFollowupFileUpload={handleFollowupFileUpload}
                  onRemoveFollowupAttachment={removeFollowupAttachment}
                  formatFileSize={formatFileSize}
                  followupFileInputRef={followupFileInputRef}
                  followupAcceptString={followupAcceptString}
                  followupFileIcons={followupFileIcons}
                  followupFilesRemaining={effectiveFollowupFilesRemaining}
                />
              )}

              {/* Empty State */}
              {!result && !loading && (
                <div className="text-center py-10 md:py-16 animate-fade-up">
                  <div className="w-12 h-12 md:w-16 md:h-16 border border-border rounded-lg flex items-center justify-center mx-auto mb-3 md:mb-4">
                    <ClipboardList className="w-6 h-6 md:w-8 md:h-8 text-primary" />
                  </div>
                  <h3 className="text-base md:text-lg font-semibold mb-2">{t('query.emptyTitle')}</h3>
                  <p className="text-xs md:text-sm text-muted-foreground max-w-md mx-auto px-4">
                    {t('query.emptyDesc')}
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
      <HistoryConsentDialog
        open={historyConsentOpen}
        onOpenChange={setHistoryConsentOpen}
        onAccepted={() => {
          const scenario = submitScenarioRef.current;
          if (scenario) handleSubmit({ scenario, alreadyTracked: true });
          else handleSubmit();
        }}
      />
      <CaseFilesNotSavedDialog
        open={historyFilesNoticeOpen}
        onConfirm={() => setHistoryFilesNoticeOpen(false)}
      />
      <PaywallModal
        open={paywallOpen}
        onOpenChange={setPaywallOpen}
        checkoutEnabled={entitlements?.checkout_enabled !== false}
        onDismiss={() => setPaywallOpen(false)}
      />
    </div>
  );
};

export default QueryPage;
export { Sidebar, MODEL_COLORS, MODEL_NAMES, BACKING_MODELS, BACKING_MODEL_NAMES };
